import { Directive, inject, input } from '@angular/core';
import { MatIcon, MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';

import { explicitEffect } from '../explicit-effect';

/**
 * This directive allows to use locally provided icons and external icon urls for the mat-icon component in a lightweight manner.
 * Usage <mat-icon icon="my-icon" [iconSize]="2">/<mat-icon>
 *
 * If you need more advanced features, consider <celum-icon> instead.
 * Note: Icons must be accessible via the /icons path, so make sure to copy them accordingly in your application project.json
 */
@Directive({
  selector: 'mat-icon[icon]',
  host: {
    '[style.width.rem]': 'this.iconWidthRem() ?? this.iconSizeRem()',
    '[style.minWidth.rem]': 'this.iconWidthRem() ?? this.iconSizeRem()',
    '[style.height.rem]': 'this.iconHeightRem() ?? this.iconSizeRem()'
  }
})
export class IconDirective {
  public icon = input<string>();
  public iconWidthRem = input<number>();
  public iconHeightRem = input<number>();
  public iconSizeRem = input<number>();

  private iconRegistry = inject(MatIconRegistry);
  private sanitizer = inject(DomSanitizer);
  private matIcon = inject(MatIcon, { host: true });

  constructor() {
    explicitEffect(this.icon, icon => {
      if (!icon) {
        return;
      }

      const { iconName, iconUrl } = this.parseIcon(icon);
      const sanitizedIconPath = this.sanitizer.bypassSecurityTrustResourceUrl(iconUrl);
      // add icon to registry only if it is not already there to avoid unnecessary requests
      this.iconRegistry.getNamedSvgIcon(iconName).subscribe({ error: () => this.iconRegistry.addSvgIcon(iconName, sanitizedIconPath) });
      this.matIcon.svgIcon = iconName;
    });
  }

  private parseIcon(icon: string): { iconName: string; iconUrl: string } {
    const isUrl = /^https?:\/\//.test(icon);
    if (!isUrl) {
      return { iconName: icon, iconUrl: `icons/${icon}.svg` };
    }

    const parts = icon.split(':');
    return { iconName: parts[1], iconUrl: icon };
  }
}
